
import { useState, useEffect } from "react";
import { 
  CheckCircle, 
  XCircle, 
  Edit, 
  Trash2, 
  Plus, 
  RefreshCw,
  DatabaseBackup
} from "lucide-react";
import { format } from "date-fns";

import { InverterApi } from "@/types/inverterApi";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

import InverterApiForm from "./InverterApiForm";

const InverterApiList = () => {
  const { toast } = useToast();
  const [apis, setApis] = useState<InverterApi[]>([]);
  const [selectedApi, setSelectedApi] = useState<InverterApi | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadApis();
  }, []);

  const loadApis = () => {
    setLoading(true);
    try {
      const storedApis = JSON.parse(localStorage.getItem("inverterApis") || "[]");
      setApis(storedApis);
    } catch (error) {
      console.error("Error loading APIs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddApi = (newApi: InverterApi) => {
    setApis((prev) => [...prev, newApi]);
  };

  const handleUpdateApi = (updatedApi: InverterApi) => {
    setApis((prev) => prev.map((api) => (api.id === updatedApi.id ? updatedApi : api)));
    setIsEditOpen(false);
  };

  const handleDeleteApi = () => {
    if (!selectedApi) return;
    
    try {
      const filteredApis = apis.filter((api) => api.id !== selectedApi.id);
      localStorage.setItem("inverterApis", JSON.stringify(filteredApis));
      setApis(filteredApis);
      
      toast({
        title: "API removida",
        description: `A API ${selectedApi.name} foi removida com sucesso.`,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao remover a API.",
        variant: "destructive",
      });
    }
    
    setIsDeleteOpen(false);
  };

  const handleSyncApi = (api: InverterApi) => {
    toast({
      title: "Sincronização iniciada",
      description: `Iniciando sincronização com a API ${api.name}.`,
    });
    
    // Simulate API sync
    setTimeout(() => {
      // Update last sync time
      const updatedApi = { 
        ...api, 
        lastSync: new Date().toISOString() 
      };
      
      // Update the API in the list
      const updatedApis = apis.map((a) => (a.id === api.id ? updatedApi : a));
      setApis(updatedApis);
      localStorage.setItem("inverterApis", JSON.stringify(updatedApis));
      
      toast({
        title: "Sincronização concluída",
        description: `Sincronização com a API ${api.name} concluída com sucesso.`,
      });
    }, 2000);
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>APIs de Inversores</CardTitle>
          <CardDescription>
            Gerenciamento de conexões com APIs de fabricantes de inversores
          </CardDescription>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Nova API
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Cadastrar Nova API</DialogTitle>
              <DialogDescription>
                Configure os dados de acesso à API do fabricante de inversores
              </DialogDescription>
            </DialogHeader>
            <InverterApiForm onSuccess={handleAddApi} />
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : apis.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <DatabaseBackup className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Nenhuma API cadastrada</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Adicione uma nova API para começar a integração com inversores
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-400px)] rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fabricante</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Última Sincronização</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apis.map((api) => (
                  <TableRow key={api.id}>
                    <TableCell className="font-medium">{api.manufacturer}</TableCell>
                    <TableCell>{api.name}</TableCell>
                    <TableCell>
                      {api.isActive ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="mr-1 h-4 w-4" />
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          <XCircle className="mr-1 h-4 w-4" />
                          Inativo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {api.lastSync ? format(new Date(api.lastSync), "dd/MM/yyyy HH:mm") : "Nunca sincronizado"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSyncApi(api)}
                          title="Sincronizar"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedApi(api);
                            setIsEditOpen(true);
                          }}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedApi(api);
                            setIsDeleteOpen(true);
                          }}
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Editar API</DialogTitle>
              <DialogDescription>
                Atualize os dados de acesso à API do fabricante de inversores
              </DialogDescription>
            </DialogHeader>
            {selectedApi && (
              <InverterApiForm
                initialData={selectedApi}
                onSuccess={handleUpdateApi}
                isEdit={true}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Alert Dialog */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir a API "{selectedApi?.name}"? Esta ação não poderá ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteApi}>Excluir</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default InverterApiList;
